import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client.js';
import { Button, Toggle } from './ds';
const fmt = (d) => new Date(d).toLocaleDateString('ru-RU');
const VERDICTS = { geo: 'Гео есть', junk: 'Мусор', needs_review: 'На проверку' };
const REASONS = {
    no_country_geo: 'Нет данных по странам',
    cities_only: 'Только города, без стран',
    unclear_geo: 'Гео нечитаемо или неоднозначно',
    invalid_rows: 'Страны не распознаны',
    invalid_percent: 'Проценты нечитаемы',
    invalid_total: 'Сумма процентов больше 100',
};
const geoStatus = (geo) => {
    if (!geo) return 'Не обработано (старая загрузка)';
    if (geo.state === 'error') return 'Ошибка распознавания';
    if (geo.state !== 'done' || !geo.result) return 'Результат не получен';
    return VERDICTS[geo.result.verdict] ?? 'Результат не получен';
};
const GeoInfo = ({ geo }) => {
    const r = geo?.state === 'done' ? geo.result : null;
    const retry = !r || !VERDICTS[r.verdict] ? 'Запросите новую загрузку' : null;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', fontSize: 'var(--fs-12)', overflowWrap: 'anywhere' }}>
            <span style={{ fontWeight: 600 }}>{geoStatus(geo)}</span>
            {retry && <span className='oc-lead-card__docs-date'>{retry}</span>}
            {r && r.verdict !== 'geo' && VERDICTS[r.verdict] && (
                <span data-geo-reason className='oc-lead-card__docs-date'>{REASONS[r.reason] ?? ''}</span>
            )}
            {r?.verdict === 'geo' && (
                <>
                    <span>Аудитория: {r.audience ?? 'Неизвестно'}</span>
                    <span>Период: {r.period_text ?? 'Неизвестно'}</span>
                    {r.rows.map((x) => (
                        <span key={x.iso2}>{x.iso2} {x.name_raw} {x.pct_text}</span>
                    ))}
                    {r.other && <span>Другое: {r.other.name_raw} {r.other.pct_text}</span>}
                    {r.undisclosed_pct > 0 && <span>Не указано (расчёт): {r.undisclosed_pct}%</span>}
                </>
            )}
        </div>
    );
};
export default function LeadCabinetSection ({ leadId, enabled }) {
    const qc = useQueryClient();
    const key = ['lead-cabinet', leadId];
    const [copied, setCopied] = useState(false);
    const { data, error, refetch, isFetching } = useQuery({
        queryKey: key,
        queryFn: async () => (await apiClient.get(`/leads/${leadId}/cabinet`)).data,
        enabled: enabled && Boolean(leadId),
        staleTime: 15_000,
    });
    const access = useMutation({
        mutationFn: async (next) => (await apiClient.patch(`/leads/${leadId}/cabinet/access`, { enabled: next })).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: key }),
        onSettled: () => setConfirmOff(false),
    });
    const [confirmOff, setConfirmOff] = useState(false);
    const switchRef = useRef(null);
    useEffect(() => {
        if (!confirmOff) return undefined;
        const onDown = (e) => { if (!switchRef.current?.contains(e.target) && !access.isPending) setConfirmOff(false); };
        const onKey = (e) => { if (e.key === 'Escape' && !access.isPending) setConfirmOff(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [confirmOff, access.isPending]);
    const copy = async (url) => {
        try { await navigator.clipboard.writeText(url); setCopied(true); } catch { setCopied(false); }
    };
    if (error?.status === 403) return null;
    const contracts = data?.contracts ?? [];
    if (contracts.length === 0) return null;
    const accounts = data?.accounts ?? [];
    const uploads = data?.uploads ?? [];
    const failure = error || access.error;
    const known = new Set(accounts.map((a) => a.id));
    const groups = [
        ...accounts.map((a) => ({ key: a.id, title: `${a.platform} · ${a.handle}`, items: uploads.filter((u) => u.trackedAccountId === a.id) })),
        { key: 'none', title: 'Без аккаунта', items: uploads.filter((u) => !known.has(u.trackedAccountId)) },
    ].filter((g) => g.items.length > 0);
    const text = { fontSize: 'var(--fs-14)', color: 'var(--text-primary)' };
    const col = { display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' };
    return (
        <div className='oc-lead-card__docs-contract oc-lead-card__cabinet'>
            <div className='oc-lead-card__docs-contract-head'>
                <span className='oc-lead-card__docs-title'>Кабинет блогера</span>
            </div>
            <div className='oc-lead-card__docs-contract-body'>
                {failure && <p className='oc-lead-card__docs-note'>{failure.message}</p>}
                {data && (
                    <div style={col}>
                        <div ref={switchRef} style={{ position: 'relative', alignSelf: 'flex-start' }}>
                            <Toggle
                                on={data.enabled}
                                disabled={access.isPending}
                                onChange={(next) => (next ? access.mutate(true) : setConfirmOff((v) => !v))}
                            >
                                Доступ к кабинету
                            </Toggle>
                            {confirmOff && (
                                <div
                                    role='dialog'
                                    aria-label='Подтверждение'
                                    style={{
                                        position: 'absolute',
                                        bottom: '100%',
                                        left: 0,
                                        marginBottom: 'var(--space-8)',
                                        zIndex: 50,
                                        width: 260,
                                        maxWidth: '80vw',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 'var(--space-8)',
                                        padding: 'var(--space-12)',
                                        background: 'var(--surface-page)',
                                        border: '1px solid var(--border-subtle)',
                                        borderRadius: 'var(--radius-8)',
                                        boxShadow: 'var(--shadow-menu)',
                                    }}
                                >
                                    <span style={text}>Вы точно хотите отозвать доступ к кабинету?</span>
                                    <span style={{ display: 'flex', gap: 'var(--space-8)' }}>
                                        <Button size='small' disabled={access.isPending} onClick={() => access.mutate(false)}>Отозвать</Button>
                                        <Button hierarchy='secondary' size='small' disabled={access.isPending} onClick={() => setConfirmOff(false)}>Отмена</Button>
                                    </span>
                                </div>
                            )}
                        </div>
                        <span style={{ display: 'flex', gap: 'var(--space-8)' }}>
                            <input
                                readOnly
                                value={data.cabinetUrl}
                                aria-label='Ссылка на кабинет'
                                onFocus={(e) => e.target.select()}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: 'var(--fs-12)',
                                    padding: 'var(--space-6) var(--space-8)',
                                    border: '1px solid var(--border-subtle)',
                                    borderRadius: 'var(--radius-8)',
                                }}
                            />
                            <Button hierarchy='secondary' size='small' onClick={() => copy(data.cabinetUrl)}>
                                {copied ? 'Скопировано' : 'Скопировать'}
                            </Button>
                        </span>
                        <span className='oc-lead-card__docs-note'>
                            Блогер открывает ссылку, вводит почту из анкеты договора и код из письма. Выключите доступ — вход закроется сразу.
                        </span>
                    </div>
                )}
                {contracts.map((c) => (
                    <span key={c.contractId} style={text}>{c.email}</span>
                ))}
                {data && groups.length > 0 && contracts.length > 0 && (
                    <span style={{ alignSelf: 'flex-start' }}>
                        <Button hierarchy='secondary' size='small' disabled={isFetching} onClick={() => refetch()}>Обновить</Button>
                    </span>
                )}
                {data && contracts.length > 0 && (
                    groups.length === 0 ? (
                        <p className='oc-lead-card__docs-note'>Скриншотов пока нет</p>
                    ) : groups.map((g) => (
                        <div key={g.key} style={col}>
                            <span style={text}>{g.title} <span className='oc-lead-card__docs-date'>· {g.items.length}</span></span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-12)' }}>
                                {g.items.map((u) => {
                                    const href = `/api/leads/${leadId}/cabinet/uploads/${u.id}`;
                                    return (
                                        <div
                                            key={u.id}
                                            data-geo-upload={u.id}
                                            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', width: 200, maxWidth: '100%' }}
                                        >
                                            <a
                                                href={href}
                                                target='_blank'
                                                rel='noreferrer'
                                                style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', color: 'inherit', textDecoration: 'none' }}
                                            >
                                                <img
                                                    src={href}
                                                    alt=''
                                                    style={{ width: 96, height: 96, objectFit: 'cover', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-8)' }}
                                                />
                                                <span className='oc-lead-card__docs-date'>{fmt(u.createdAt)}</span>
                                            </a>
                                            <GeoInfo geo={u.geo} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
